import SendIcon from "@mui/icons-material/Send";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import Rating from "@mui/lab/Rating";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useAuthUser } from "../../../context/GameAuthContext";
import { apiClient } from "../../../utils/apiClient";
import { useContent } from "../details/utils";
import { CommentText, Container, Fieldset, RatingSystemCommentTitle, SubmitButton, Title } from "./styles";

const SmileyRating = styled(Rating)(({ theme }) => ({
	icon: {
		color: theme.palette.common.black,
		fontSize: "2rem",
	},
	iconFilled: {
		color: theme.palette.primary.main,
		fontSize: "2rem",
	},
	iconHover: {
		color: theme.palette.primary.main,
		fontSize: "2rem",
	},
}));

const customIcons = {
	1: {
		icon: <SentimentVeryDissatisfiedIcon fontSize="inherit" />,
		label: "GAME_OVER.rating.star-one.label",
		dataTestId: `rating-value-1`,
	},
	2: {
		icon: <SentimentDissatisfiedIcon fontSize="inherit" />,
		label: "GAME_OVER.rating.star-two.label",
		dataTestId: `rating-value-2`,
	},
	3: {
		icon: <SentimentSatisfiedIcon fontSize="inherit" />,
		label: "GAME_OVER.rating.star-three.label",
		dataTestId: `rating-value-3`,
	},
	4: {
		icon: <SentimentSatisfiedAltIcon fontSize="inherit" />,
		label: "GAME_OVER.rating.star-four.label",
		dataTestId: `rating-value-4`,
	},
	5: {
		icon: <SentimentVerySatisfiedIcon fontSize="inherit" />,
		label: "GAME_OVER.rating.star-five.label",
		dataTestId: `rating-value-5`,
	},
};

function IconContainer(props: any) {
	const { value, ...other } = props;
	return (
		<div data-testid={customIcons[value].dataTestId} {...other}>
			{customIcons[value].icon}
		</div>
	);
}

type FeedbackData = {
	ratings: number;
	comments: string;
	roomId: string;
	userId: string;
};

const submitRatings = async (data: FeedbackData, token: string) => {
	const response = await apiClient("feedback", {
		data,
		token,
	});
	return response;
};

type Props = { gameOverContent: any | undefined };

const CustomizedRatings = ({ gameOverContent }: Props) => {
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({ mode: "onSubmit" });
	const [isLoading, setLoading] = useState<boolean>(false);
	const { roomId, id, token } = useAuthUser();
	const { enqueueSnackbar } = useSnackbar();
	const [sendDisabled, setSendDisabled] = useState<boolean>(false);
	const intl = useIntl();

	const contentTitle = useContent({
		contentId: gameOverContent?.feedbackTitle,
		defaultId: "GAME_OVER.rating.comment.header",
	});
	const contentComment = useContent({
		contentId: gameOverContent?.feedbackComment,
		defaultId: "GAME_OVER.rating.comment.title",
	});
	const contentButton = useContent({
		contentId: gameOverContent?.feedbackButton,
		defaultId: "GAME_OVER.rating.btn.submit",
	});

	useEffect(() => {
		if (errors?.ratings?.type === "min") {
			enqueueSnackbar(intl.formatMessage({ id: "GAME_OVER.rating.error.message.required" }), {
				variant: "error",
			});
		}
	}, [errors, enqueueSnackbar, intl]);

	const onSubmit: SubmitHandler<FeedbackData> = async (data, e) => {
		try {
			setLoading(true);
			data.roomId = roomId;
			data.userId = id;
			const response = await submitRatings(data, token);
			if (response.data) {
				enqueueSnackbar(intl.formatMessage({ id: "GAME_OVER.rating.success.message" }), {
					variant: "success",
					//@ts-ignore
					"data-testid": "successful-feedback",
				});
			}
			reset({ ratings: 0, comments: "" });
		} catch (err) {
			if ((err as any).status === 409) {
				enqueueSnackbar(intl.formatMessage({ id: "GAME_OVER.rating.error.message.already_submitted" }), {
					variant: "error",
				});
			} else {
				enqueueSnackbar(intl.formatMessage({ id: "GAME_OVER.rating.error.message" }), { variant: "error" });
			}
		} finally {
			setLoading(false);
		}
	};

	function handleMessageDisable(e: React.ChangeEvent<HTMLInputElement>) {
		isMobile && e.target.value.length > 0 ? setSendDisabled(true) : setSendDisabled(false);
	}

	return (
		<Container
			//@ts-ignore
			component="form"
			data-testid="feedback-form"
			onSubmit={handleSubmit(onSubmit)}
		>
			<Fieldset component="fieldset">
				<Title variant="h5">{contentTitle}</Title>
				<Controller
					name="ratings"
					control={control}
					defaultValue={0}
					rules={{ required: true, min: 1, max: 5 }}
					render={(props) => {
						return (
							<SmileyRating
								max={5}
								getLabelText={(value) => intl.formatMessage({ id: customIcons[value].label })}
								IconContainerComponent={IconContainer}
								{...props}
							/>
						);
					}}
				/>
				<RatingSystemCommentTitle>{contentComment}</RatingSystemCommentTitle>
				<Box display="flex" mb={2} flexDirection="column" style={{ position: "relative" }}>
					<Controller
						name="comments"
						control={control}
						render={({ onChange, ...props }) => (
							<CommentText
								variant="outlined"
								data-testid="feedback-form-comment"
								id="feedback-form-comment"
								rows={isDesktop ? 3 : 1}
								multiline
								onChange={(e) => {
									onChange(e.target.value);
									handleMessageDisable(e as React.ChangeEvent<HTMLInputElement>);
								}}
								{...props}
							/>
						)}
					/>
					{isMobile && (
						<SubmitButton id="button-submit-review-rating" type="submit" disabled={isLoading}>
							<SendIcon
								style={{
									color: sendDisabled ? "black" : "grey",
								}}
							/>
						</SubmitButton>
					)}
				</Box>
				<Box display="flex" justifyContent="flex-end">
					{isDesktop && (
						<SubmitButton id="button-submit-review-rating" type="submit" disabled={isLoading}>
							{contentButton}
						</SubmitButton>
					)}
				</Box>
			</Fieldset>
		</Container>
	);
};

export default CustomizedRatings;
